import React from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { TabView, TabPanel } from 'primereact/tabview';
import { FileUpload, type FileUploadSelectEvent } from 'primereact/fileupload';
import { type Toast } from 'primereact/toast';
import OpenAPI from '../../../endpoint/Openapi';
import { type paths, type components } from '../../../endpoint/Schema';
import { normalizeString } from '../../../utils/GeneralUtils';
import { type CustomError } from '../../../models/Error';

// Interface for selected image files with preview
interface SelectedImage {
  file: File;
  previewUrl: string;
  name: string;
}

// Type for GooglePlayConfig
type GooglePlayConfig = NonNullable<
  NonNullable<
    paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
  >['googlePlayConfig']
>;

// Interface for existing server images
interface ExistingImage {
  id?: string;
  url: string;
  name: string;
  index: number;
  isDeleted: boolean;
}

// Type for FeatureSettings
type FeatureSettings =
  paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json'];

// Type for TenantData
type TenantData = components['schemas']['TenantInfoDto'];

interface GooglePlayStoreTabProps {
  googleConfig: GooglePlayConfig;
  featureSettings: FeatureSettings | undefined;
  setFeatureSettings: React.Dispatch<
    React.SetStateAction<FeatureSettings | undefined>
  >;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  tenantId: string | undefined;
  tenantData: TenantData;
  toast: React.RefObject<Toast>;
  fetchSettings: () => Promise<void>;
  activeTabIndex: number;
  setActiveTabIndex: React.Dispatch<React.SetStateAction<number>>;
}

export default function GooglePlayStoreTab({
  googleConfig,
  featureSettings,
  setFeatureSettings,
  loading,
  setLoading,
  tenantId,
  tenantData,
  toast,
  fetchSettings,
  activeTabIndex,
  setActiveTabIndex,
}: GooglePlayStoreTabProps) {
  // State for selected images with previews
  const [googleSelectedImages, setGoogleSelectedImages] = React.useState<
    SelectedImage[]
  >([]);

  // Refs for FileUpload components to clear them after selection
  const googleFileUploadRef = React.useRef<FileUpload>(null);

  // Keys to force FileUpload component re-render when needed
  const [googleUploadKey, setGoogleUploadKey] = React.useState(0);

  // State for existing server screenshots
  const [existingImages, setExistingImages] = React.useState<ExistingImage[]>(
    [],
  );

  // State for Delete Account Page Screenshots
  const [deleteAccountSelectedImages, setDeleteAccountSelectedImages] =
    React.useState<SelectedImage[]>([]);
  const [deleteAccountExistingImages, setDeleteAccountExistingImages] =
    React.useState<ExistingImage[]>([]);
  const [deleteAccountUploadKey, setDeleteAccountUploadKey] = React.useState(0);
  const deleteAccountFileUploadRef = React.useRef<FileUpload>(null);

  // Drag and drop state for google play screenshots
  const [googleDraggedIndex, setGoogleDraggedIndex] = React.useState<
    number | null
  >(null);
  const [googleDragOverIndex, setGoogleDragOverIndex] = React.useState<
    number | null
  >(null);
  const [googleHasOrderChanged, setGoogleHasOrderChanged] =
    React.useState(false);

  // Drag and drop state for delete account screenshots
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);

  // State to track if order has been changed to show save button
  const [hasOrderChanged, setHasOrderChanged] = React.useState(false);

  // State for Feature Graphic - single image only
  const [featureGraphicSelectedImage, setFeatureGraphicSelectedImage] =
    React.useState<SelectedImage | null>(null);
  const [featureGraphicExistingImage, setFeatureGraphicExistingImage] =
    React.useState<ExistingImage | null>(null);
  const [featureGraphicUploadKey, setFeatureGraphicUploadKey] =
    React.useState(0);
  const featureGraphicFileUploadRef = React.useRef<FileUpload>(null);

  // Handlers for Google Play Config
  const handleGoogleConfigChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFeatureSettings((prev: FeatureSettings | undefined) => ({
      ...prev,
      googlePlayConfig: {
        ...prev?.googlePlayConfig,
        [name]: value ?? '',
      },
    }));
  };

  // File upload handlers with PrimeReact FileUpload
  const handleGoogleScreenshotsSelect = (e: FileUploadSelectEvent) => {
    const files = e.files;
    if (!files || files.length === 0) return;

    const newImages: SelectedImage[] = Array.from(files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
    }));

    // Add new images to existing ones instead of replacing
    setGoogleSelectedImages((prev) => [...prev, ...newImages]);

    // Force FileUpload component to re-render by changing key
    setGoogleUploadKey((prev) => prev + 1);
  };

  // Remove image handler
  const handleRemoveGoogleImage = (index: number) => {
    setGoogleSelectedImages((prev) => {
      const imageToRemove = prev[index];
      // Clean up the object URL to prevent memory leaks
      URL.revokeObjectURL(imageToRemove.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Clear all images handler
  const handleClearAllGoogleImages = () => {
    // Clean up all object URLs
    googleSelectedImages.forEach((image) => {
      URL.revokeObjectURL(image.previewUrl);
    });

    setGoogleSelectedImages([]);
  };

  // Remove existing server image handler
  const handleRemoveExistingImage = (imageIndex: number) => {
    setExistingImages((prev) =>
      prev.map((img) => {
        // Mark the specific image for deletion using its index
        if (img.index === imageIndex) {
          return { ...img, isDeleted: true };
        }
        return img;
      }),
    );
  };

  // Delete Account Screenshot Handlers
  const handleDeleteAccountScreenshotsSelect = (e: FileUploadSelectEvent) => {
    const files = e.files;
    if (!files || files.length === 0) return;

    const newImages: SelectedImage[] = Array.from(files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
    }));

    // Add new images to existing ones instead of replacing
    setDeleteAccountSelectedImages((prev) => [...prev, ...newImages]);

    // Force FileUpload component to re-render by changing key
    setDeleteAccountUploadKey((prev) => prev + 1);

    // Clear the FileUpload component
    if (deleteAccountFileUploadRef.current) {
      deleteAccountFileUploadRef.current.clear();
    }
  };

  const handleRemoveDeleteAccountImage = (index: number) => {
    setDeleteAccountSelectedImages((prev) => {
      const updated = [...prev];
      // Revoke the object URL to free memory
      URL.revokeObjectURL(updated[index].previewUrl);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleClearAllDeleteAccountImages = () => {
    deleteAccountSelectedImages.forEach((image) => {
      URL.revokeObjectURL(image.previewUrl);
    });

    setDeleteAccountSelectedImages([]);
  };

  const handleRemoveDeleteAccountExistingImage = (imageIndex: number) => {
    setDeleteAccountExistingImages((prev) =>
      prev.map((img) => {
        if (img.index === imageIndex) {
          return { ...img, isDeleted: true };
        }
        return img;
      }),
    );
  };

  // Move image up in order for Google Play screenshots
  const moveGooglePlayImageUp = (displayIndex: number) => {
    if (displayIndex === 0) return; // Can't move first item up

    setExistingImages((prev) => {
      const nonDeletedImages = prev.filter((img) => !img.isDeleted);
      if (displayIndex >= nonDeletedImages.length) return prev;

      const updated = [...prev];
      // Get the actual indices in the full array
      const currentImageIndex = prev.findIndex(
        (img) => img === nonDeletedImages[displayIndex],
      );
      const previousImageIndex = prev.findIndex(
        (img) => img === nonDeletedImages[displayIndex - 1],
      );

      if (currentImageIndex === -1 || previousImageIndex === -1) return prev;

      // Swap the images
      [updated[currentImageIndex], updated[previousImageIndex]] = [
        updated[previousImageIndex],
        updated[currentImageIndex],
      ];

      // Update order values based on new positions (starting from 0)
      updated[currentImageIndex].index = displayIndex - 1;
      updated[previousImageIndex].index = displayIndex;

      // Mark that order has changed
      setGoogleHasOrderChanged(true);

      return updated;
    });
  };

  // Move image down in order for Google Play screenshots
  const moveGooglePlayImageDown = (displayIndex: number) => {
    setExistingImages((prev) => {
      const nonDeletedImages = prev.filter((img) => !img.isDeleted);
      if (displayIndex >= nonDeletedImages.length - 1) return prev; // Can't move last item down

      const updated = [...prev];
      // Get the actual indices in the full array
      const currentImageIndex = prev.findIndex(
        (img) => img === nonDeletedImages[displayIndex],
      );
      const nextImageIndex = prev.findIndex(
        (img) => img === nonDeletedImages[displayIndex + 1],
      );

      if (currentImageIndex === -1 || nextImageIndex === -1) return prev;

      // Swap the images
      [updated[currentImageIndex], updated[nextImageIndex]] = [
        updated[nextImageIndex],
        updated[currentImageIndex],
      ];

      // Update order values based on new positions (starting from 0)
      updated[currentImageIndex].index = displayIndex + 1;
      updated[nextImageIndex].index = displayIndex;

      // Mark that order has changed
      setGoogleHasOrderChanged(true);

      return updated;
    });
  };

  // Drag and drop handlers for google play screenshots
  const handleGooglePlayDragStart = (
    e: React.DragEvent,
    displayIndex: number,
  ) => {
    setGoogleDraggedIndex(displayIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleGooglePlayDragEnter = (
    e: React.DragEvent,
    displayIndex: number,
  ) => {
    e.preventDefault();
    setGoogleDragOverIndex(displayIndex);
  };

  const handleGooglePlayDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleGooglePlayDragLeave = (e: React.DragEvent) => {
    // Only clear drag over if we're actually leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setGoogleDragOverIndex(null);
    }
  };

  const handleGooglePlayDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (googleDraggedIndex === null || googleDraggedIndex === dropIndex) {
      setGoogleDraggedIndex(null);
      setGoogleDragOverIndex(null);
      return;
    }

    setExistingImages((prev) => {
      const nonDeletedImages = prev.filter((img) => !img.isDeleted);
      const updated = [...prev];

      // Get the actual images being moved
      const draggedImage = nonDeletedImages[googleDraggedIndex];
      const dropTargetImage = nonDeletedImages[dropIndex];

      // Find their actual indices in the full array
      const draggedActualIndex = prev.findIndex((img) => img === draggedImage);
      const dropActualIndex = prev.findIndex((img) => img === dropTargetImage);

      if (draggedActualIndex === -1 || dropActualIndex === -1) return prev;

      // Remove the dragged item
      const [removedItem] = updated.splice(draggedActualIndex, 1);

      // Insert it at the new position
      const newDropIndex =
        draggedActualIndex < dropActualIndex
          ? dropActualIndex - 1
          : dropActualIndex;
      updated.splice(newDropIndex, 0, removedItem);

      // Update all order indices for non-deleted images (starting from 0)
      const updatedNonDeleted = updated.filter((img) => !img.isDeleted);
      updatedNonDeleted.forEach((img, index) => {
        const actualIndex = updated.findIndex((u) => u === img);
        if (actualIndex !== -1) {
          updated[actualIndex].index = index;
        }
      });

      // Mark that order has changed
      setGoogleHasOrderChanged(true);

      return updated;
    });

    setGoogleDraggedIndex(null);
    setGoogleDragOverIndex(null);
  };

  const handleGooglePlayDragEnd = () => {
    setGoogleDraggedIndex(null);
    setGoogleDragOverIndex(null);
  };

  // Move image up in order
  const moveDeleteAccountImageUp = (displayIndex: number) => {
    if (displayIndex === 0) return; // Can't move first item up

    setDeleteAccountExistingImages((prev) => {
      const nonDeletedImages = prev.filter((img) => !img.isDeleted);
      if (displayIndex >= nonDeletedImages.length) return prev;

      const updated = [...prev];
      // Get the actual indices in the full array
      const currentImageIndex = prev.findIndex(
        (img) => img === nonDeletedImages[displayIndex],
      );
      const previousImageIndex = prev.findIndex(
        (img) => img === nonDeletedImages[displayIndex - 1],
      );

      if (currentImageIndex === -1 || previousImageIndex === -1) return prev;

      // Swap the images
      [updated[currentImageIndex], updated[previousImageIndex]] = [
        updated[previousImageIndex],
        updated[currentImageIndex],
      ];

      // Update order values based on new positions
      updated[currentImageIndex].index = displayIndex - 1;
      updated[previousImageIndex].index = displayIndex;

      // Mark that order has changed
      setHasOrderChanged(true);

      return updated;
    });
  };

  // Move image down in order
  const moveDeleteAccountImageDown = (displayIndex: number) => {
    setDeleteAccountExistingImages((prev) => {
      const nonDeletedImages = prev.filter((img) => !img.isDeleted);
      if (displayIndex >= nonDeletedImages.length - 1) return prev; // Can't move last item down

      const updated = [...prev];
      // Get the actual indices in the full array
      const currentImageIndex = prev.findIndex(
        (img) => img === nonDeletedImages[displayIndex],
      );
      const nextImageIndex = prev.findIndex(
        (img) => img === nonDeletedImages[displayIndex + 1],
      );

      if (currentImageIndex === -1 || nextImageIndex === -1) return prev;

      // Swap the images
      [updated[currentImageIndex], updated[nextImageIndex]] = [
        updated[nextImageIndex],
        updated[currentImageIndex],
      ];

      // Update order values based on new positions
      updated[currentImageIndex].index = displayIndex + 1;
      updated[nextImageIndex].index = displayIndex;

      // Mark that order has changed
      setHasOrderChanged(true);

      return updated;
    });
  };

  // Drag and drop handlers for delete account screenshots
  const handleDragStart = (e: React.DragEvent, displayIndex: number) => {
    setDraggedIndex(displayIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragEnter = (e: React.DragEvent, displayIndex: number) => {
    e.preventDefault();
    setDragOverIndex(displayIndex);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear drag over if we're actually leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setDeleteAccountExistingImages((prev) => {
      const nonDeletedImages = prev.filter((img) => !img.isDeleted);
      const updated = [...prev];

      // Get the actual images being moved
      const draggedImage = nonDeletedImages[draggedIndex];
      const dropTargetImage = nonDeletedImages[dropIndex];

      // Find their actual indices in the full array
      const draggedActualIndex = prev.findIndex((img) => img === draggedImage);
      const dropActualIndex = prev.findIndex((img) => img === dropTargetImage);

      if (draggedActualIndex === -1 || dropActualIndex === -1) return prev;

      // Remove the dragged item
      const [removedItem] = updated.splice(draggedActualIndex, 1);

      // Insert it at the new position
      const newDropIndex =
        draggedActualIndex < dropActualIndex
          ? dropActualIndex - 1
          : dropActualIndex;
      updated.splice(newDropIndex, 0, removedItem);

      // Update all order indices for non-deleted images
      const updatedNonDeleted = updated.filter((img) => !img.isDeleted);
      updatedNonDeleted.forEach((img, index) => {
        const actualIndex = updated.findIndex((u) => u === img);
        if (actualIndex !== -1) {
          updated[actualIndex].index = index;
        }
      });

      // Mark that order has changed
      setHasOrderChanged(true);

      return updated;
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Feature Graphic Handlers
  const handleFeatureGraphicSelect = (e: FileUploadSelectEvent) => {
    const files = e.files;
    if (!files || files.length === 0) return;

    // Only take the first file since we want single image
    const file = files[0];
    const newImage: SelectedImage = {
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
    };

    // Clear any existing selected image first
    if (featureGraphicSelectedImage) {
      URL.revokeObjectURL(featureGraphicSelectedImage.previewUrl);
    }

    setFeatureGraphicSelectedImage(newImage);

    // Force FileUpload component to re-render by changing key
    setFeatureGraphicUploadKey((prev) => prev + 1);

    // Clear the FileUpload component
    if (featureGraphicFileUploadRef.current) {
      featureGraphicFileUploadRef.current.clear();
    }
  };

  const handleRemoveFeatureGraphicImage = () => {
    if (featureGraphicSelectedImage) {
      URL.revokeObjectURL(featureGraphicSelectedImage.previewUrl);
      setFeatureGraphicSelectedImage(null);
    }
  };

  const handleRemoveFeatureGraphicExistingImage = () => {
    if (featureGraphicExistingImage) {
      setFeatureGraphicExistingImage((prev) =>
        prev ? { ...prev, isDeleted: true } : null,
      );
    }
  };

  // Save Google Play App Information function
  async function saveGooglePlayInformation() {
    if (!tenantId) return;

    // Normalize and validate fields
    const appShortDescription = normalizeString(
      googleConfig?.appShortDescription,
    );
    const website = normalizeString(googleConfig?.website);
    const phoneNumber = normalizeString(googleConfig?.phoneNumber);
    const youTubeLink = normalizeString(googleConfig?.youTubeLink);
    const appFullDescription = normalizeString(
      googleConfig?.appFullDescription,
    );

    setLoading(true);

    try {
      const res = await OpenAPI.POST('/api/v2/admin/feature-setting', {
        params: {
          query: {
            tenantId,
          },
        },
        body: {
          googlePlayConfig: {
            appShortDescription: appShortDescription ?? null,
            website: website ?? null,
            phoneNumber: phoneNumber ?? null,
            youTubeLink: youTubeLink ?? null,
            appFullDescription: appFullDescription ?? null,
          },
        },
      });

      // @ts-expect-error
      if (res.error) throw res.error;

      toast.current?.show({
        severity: 'success',
        summary: 'Google Play Information saved successfully',
        life: 3000,
      });

      if (res.data) {
        setFeatureSettings(res.data);
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setLoading(false);
  }

  // Save Google Play Screenshots function
  async function saveGooglePlayScreenshots() {
    if (!tenantId) return;

    setLoading(true);

    try {
      // Create DTOs for all screenshots
      const allDtos: Array<{
        file?: File;
        id?: string;
        order?: number;
        isDeleted: boolean;
      }> = [];

      // First, create a combined array of all images (existing + new) to determine proper order
      const allImages: Array<{
        isNew: boolean;
        image?: SelectedImage;
        existingImage?: ExistingImage;
        currentOrder: number;
      }> = [];

      // Add existing non-deleted images with their current order
      existingImages
        .filter((img) => !img.isDeleted)
        .forEach((image) => {
          allImages.push({
            isNew: false,
            existingImage: image,
            currentOrder: image.index,
          });
        });

      // Add new images at the end
      googleSelectedImages.forEach((image, index) => {
        allImages.push({
          isNew: true,
          image,
          currentOrder: allImages.length,
        });
      });

      // Sort by current order to maintain the intended sequence
      allImages.sort((a, b) => a.currentOrder - b.currentOrder);

      // Now create DTOs with proper 0-based ordering
      allImages.forEach((item, finalOrder) => {
        if (item.isNew && item.image) {
          // New image
          allDtos.push({
            file: item.image.file,
            order: finalOrder,
            isDeleted: false,
          });
        } else if (!item.isNew && item.existingImage) {
          // Existing image
          const dto: {
            id?: string;
            order?: number;
            isDeleted: boolean;
          } = {
            order: finalOrder,
            isDeleted: false,
          };

          // Include ID if it exists and is not the problematic default UUID
          if (
            item.existingImage.id &&
            typeof item.existingImage.id === 'string' &&
            item.existingImage.id.trim() !== '' &&
            item.existingImage.id !== '00000000-0000-0000-0000-000000000000'
          ) {
            dto.id = item.existingImage.id;
          }

          allDtos.push(dto);
        }
      });

      // Add deleted images to the DTOs (they won't affect the ordering of active images)
      existingImages
        .filter((img) => img.isDeleted)
        .forEach((image) => {
          const dto: {
            id?: string;
            order?: number;
            isDeleted: boolean;
          } = {
            order: image.index, // Keep original order for deletion tracking
            isDeleted: true,
          };

          // Include ID if it exists and is not the problematic default UUID
          if (
            image.id &&
            typeof image.id === 'string' &&
            image.id.trim() !== '' &&
            image.id !== '00000000-0000-0000-0000-000000000000'
          ) {
            dto.id = image.id;
          }

          allDtos.push(dto);
        });

      const res = await OpenAPI.POST(
        '/api/v2/admin/feature-setting/google-play-screen-shot',
        {
          params: {
            query: {
              tenantId,
            },
          },
          body: {
            // @ts-expect-error - File type vs string type mismatch in schema
            dto: allDtos,
          },
          bodySerializer: (body) => {
            if (!body?.dto) return body;
            const formData = new FormData();
            body.dto.forEach((item, index: number) => {
              // For new images (uploads)
              if (item.file && !item.isDeleted) {
                formData.append(
                  `dto[${index}].file`,
                  item.file as unknown as File,
                );
              }
              // For existing images, include the ID if present
              if (item.id !== undefined && item.id !== null) {
                formData.append(`dto[${index}].id`, item.id);
              }
              // Always include order/index
              if (item.order !== undefined) {
                formData.append(`dto[${index}].order`, item.order.toString());
              }
              // Always include deletion status
              if (item.isDeleted !== undefined) {
                formData.append(
                  `dto[${index}].isDeleted`,
                  item.isDeleted.toString(),
                );
              }
            });
            return formData;
          },
        },
      );

      // @ts-expect-error
      if (res.error) throw res.error;

      toast.current?.show({
        severity: 'success',
        summary: 'Google Play Screenshots saved successfully',
        life: 3000,
      });

      // Clear the selected images after successful save
      setGoogleSelectedImages([]);
      setGoogleUploadKey((prev) => prev + 1);

      // Reset order change flag after successful save
      setGoogleHasOrderChanged(false);

      // Reset existing images deletion state and refresh data
      setExistingImages([]);
      await fetchSettings();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setLoading(false);
  }

  // Save Delete Account Page Screenshots function
  async function saveDeleteAccountScreenshots() {
    if (!tenantId) return;

    setLoading(true);

    try {
      // Create DTOs for all delete account screenshots with order
      const allDtos: Array<{
        file?: File;
        id?: string;
        order?: number;
        isDeleted: boolean;
      }> = [];

      // Add new images as DTOs with files
      deleteAccountSelectedImages.forEach((image, index) => {
        allDtos.push({
          file: image.file,
          order:
            deleteAccountExistingImages.filter((img) => !img.isDeleted).length +
            index,
          isDeleted: false,
        });
      });

      // Add existing images with their current order and deletion status
      deleteAccountExistingImages.forEach((image) => {
        allDtos.push({
          id: image.id,
          order: image.index,
          isDeleted: image.isDeleted,
        });
      });

      const res = await OpenAPI.POST(
        '/api/v2/admin/feature-setting/android-delete-account-screen-shot',
        {
          params: {
            query: {
              tenantId,
            },
          },
          body: {
            // @ts-expect-error - File type vs string type mismatch in schema
            dto: allDtos,
          },
          bodySerializer: (body) => {
            if (!body?.dto) return body;
            const formData = new FormData();
            body.dto.forEach((item, index: number) => {
              // For new images (uploads)
              if (item.file && !item.isDeleted) {
                formData.append(
                  `dto[${index}].file`,
                  item.file as unknown as File,
                );
              }
              // For existing images being deleted, include the ID
              if (item.id !== undefined && item.id !== null) {
                formData.append(`dto[${index}].id`, item.id);
              }
              // Always include order/index
              if (item.order !== undefined) {
                formData.append(`dto[${index}].order`, item.order.toString());
              }
              // Always include deletion status
              if (item.isDeleted !== undefined) {
                formData.append(
                  `dto[${index}].isDeleted`,
                  item.isDeleted.toString(),
                );
              }
            });
            return formData;
          },
        },
      );

      // @ts-expect-error
      if (res.error) throw res.error;

      toast.current?.show({
        severity: 'success',
        summary: 'Delete Account Screenshots saved successfully',
        life: 3000,
      });

      // Clear the selected images after successful save
      setDeleteAccountSelectedImages([]);
      setDeleteAccountUploadKey((prev) => prev + 1);

      // Reset order change flag after successful save
      setHasOrderChanged(false);

      // Reset existing images deletion state and refresh data
      setDeleteAccountExistingImages([]);
      await fetchSettings();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setLoading(false);
  }

  // Save Feature Graphic function
  async function saveFeatureGraphic() {
    if (!tenantId) return;

    setLoading(true);

    try {
      const formData = new FormData();

      // Add new image if selected
      if (featureGraphicSelectedImage) {
        formData.append('file', featureGraphicSelectedImage.file);
      }

      // Add existing image deletion status if exists
      if (featureGraphicExistingImage) {
        if (featureGraphicExistingImage.id) {
          formData.append('id', featureGraphicExistingImage.id);
        }
        formData.append(
          'isDeleted',
          featureGraphicExistingImage.isDeleted.toString(),
        );
      }

      const res = await OpenAPI.POST(
        '/api/v2/admin/feature-setting/google-play-feature-graphic',
        {
          params: {
            query: {
              tenantId,
            },
          },
          // @ts-expect-error - FormData vs schema type mismatch
          body: formData,
          bodySerializer: (body) => body, // FormData is already serialized
        },
      );

      // @ts-expect-error
      if (res.error) throw res.error;

      toast.current?.show({
        severity: 'success',
        summary: 'Feature Graphic saved successfully',
        life: 3000,
      });

      // Clear the selected image after successful save
      if (featureGraphicSelectedImage) {
        URL.revokeObjectURL(featureGraphicSelectedImage.previewUrl);
        setFeatureGraphicSelectedImage(null);
      }
      setFeatureGraphicUploadKey((prev) => prev + 1);

      // Reset existing image deletion state and refresh data
      setFeatureGraphicExistingImage(null);
      await fetchSettings();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setLoading(false);
  }

  // Cleanup object URLs when component unmounts
  React.useEffect(() => {
    return () => {
      googleSelectedImages.forEach((image) => {
        URL.revokeObjectURL(image.previewUrl);
      });
      deleteAccountSelectedImages.forEach((image) => {
        URL.revokeObjectURL(image.previewUrl);
      });
      if (featureGraphicSelectedImage) {
        URL.revokeObjectURL(featureGraphicSelectedImage.previewUrl);
      }
    };
  }, [
    googleSelectedImages,
    deleteAccountSelectedImages,
    featureGraphicSelectedImage,
  ]);

  // Load existing screenshots from featureSettings
  React.useEffect(() => {
    if (featureSettings?.googlePlayConfig?.appScreenShot) {
      const screenshots = featureSettings.googlePlayConfig.appScreenShot;
      const baseUrl = process.env.REACT_APP_API_BASE_URI ?? '';
      const existingScreenshots: ExistingImage[] = [];

      screenshots.forEach((screenshot, index) => {
        // Handle new AppScreenShotDto structure with id and url
        if (
          typeof screenshot === 'object' &&
          screenshot !== null &&
          'url' in screenshot
        ) {
          const screenshotObj = screenshot as {
            id?: string;
            url: string;
            order?: number;
          };
          const url = screenshotObj.url;
          if (typeof url === 'string') {
            existingScreenshots.push({
              id: screenshotObj.id,
              url: url.startsWith('http') ? url : `${baseUrl}${url}`,
              name: `Screenshot ${index + 1}`,
              index: screenshotObj.order ?? index, // Use order from API if available, fallback to array index
              isDeleted: false,
            });
          }
        }
      });

      // Sort screenshots by their order/index to ensure proper display order
      existingScreenshots.sort((a, b) => a.index - b.index);

      setExistingImages(existingScreenshots);
    } else {
      setExistingImages([]);
    }
  }, [featureSettings]);

  // Load existing delete account screenshots from featureSettings
  React.useEffect(() => {
    if (featureSettings?.googlePlayConfig?.androidDeleteAccountScreenShot) {
      const screenshots =
        featureSettings.googlePlayConfig.androidDeleteAccountScreenShot;
      const baseUrl = process.env.REACT_APP_API_BASE_URI ?? '';
      const existingDeleteAccountScreenshots: ExistingImage[] = [];

      screenshots.forEach((screenshot, index) => {
        // Handle AndroidDeleteAccountScreenShotDto structure
        if (
          typeof screenshot === 'object' &&
          screenshot !== null &&
          'url' in screenshot
        ) {
          const screenshotObj = screenshot as {
            id?: string;
            url: string;
            order?: number;
          };
          const url = screenshotObj.url;
          if (typeof url === 'string') {
            existingDeleteAccountScreenshots.push({
              id: screenshotObj.id,
              url: url.startsWith('http') ? url : `${baseUrl}${url}`,
              name: `Delete Account Screenshot ${index + 1}`,
              index: screenshotObj.order ?? index, // Use order from API if available, fallback to array index
              isDeleted: false,
            });
          }
        }
      });

      // Sort screenshots by their order/index to ensure proper display order
      existingDeleteAccountScreenshots.sort((a, b) => a.index - b.index);

      setDeleteAccountExistingImages(existingDeleteAccountScreenshots);
    } else {
      setDeleteAccountExistingImages([]);
    }
  }, [featureSettings]);

  // Load existing feature graphic from featureSettings
  React.useEffect(() => {
    if (featureSettings?.googlePlayConfig?.appFeatureGraphicUrl) {
      const featureGraphicUrl =
        featureSettings.googlePlayConfig.appFeatureGraphicUrl;
      const baseUrl = process.env.REACT_APP_API_BASE_URI ?? '';

      if (typeof featureGraphicUrl === 'string' && featureGraphicUrl.trim()) {
        setFeatureGraphicExistingImage({
          url: featureGraphicUrl.startsWith('http')
            ? featureGraphicUrl
            : `${baseUrl}${featureGraphicUrl}`,
          name: 'Feature Graphic',
          index: 0,
          isDeleted: false,
        });
      } else {
        setFeatureGraphicExistingImage(null);
      }
    } else {
      setFeatureGraphicExistingImage(null);
    }
  }, [featureSettings]);

  return (
    <TabView
      activeIndex={activeTabIndex}
      onTabChange={(e) => {
        setActiveTabIndex(e.index);
      }}
    >
      <TabPanel header="📝 App Information">
        <div className="flex flex-col gap-y-6">
          {/* Delete Account Page URL */}
          <div className="w-[40%] max-w-xl min-w-96">
            <div className="flex items-center justify-between">
              <span>Delete Account Page</span>
              {/* Copy Button */}
              <button
                type="button"
                onClick={() => {
                  const url = `${process.env.REACT_APP_API_BASE_URI}/delete-account/${tenantId ?? ''}`;
                  void navigator.clipboard.writeText(url);
                }}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                title="Copy URL"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
              </button>
            </div>
            <a
              href={`${process.env.REACT_APP_API_BASE_URI}/delete-account/${tenantId ?? ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-2 text-blue-600 underline break-all"
            >
              {`${process.env.REACT_APP_API_BASE_URI}/delete-account/${tenantId ?? ''}`}
            </a>
          </div>

          {/* Icon512 Display */}
          <div className="w-[40%] max-w-xl min-w-96">
            <div>App Icon (512x512)</div>
            {tenantData?.appAssets?.find((asset) => asset.name === 'Icon512')
              ?.url ? (
              <div className="mt-3">
                <img
                  src={
                    tenantData.appAssets.find(
                      (asset) => asset.name === 'Icon512',
                    )?.url
                  }
                  alt="App Icon 512x512"
                  className="w-32 h-32 object-cover rounded-lg border border-gray-200 shadow-sm"
                />
              </div>
            ) : (
              <div className="mt-3 w-32 h-32 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm">
                No Icon512 available
              </div>
            )}
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>Short Description</div>
            <InputText
              name="appShortDescription"
              value={googleConfig?.appShortDescription ?? ''}
              onChange={handleGoogleConfigChange}
              placeholder="Brief app description (max 80 characters)"
              maxLength={80}
              className="mt-3 p-inputtext-lg w-full"
            />
            <div className="text-xs text-gray-500 mt-1">
              {googleConfig?.appShortDescription?.length ?? 0}/80 characters
            </div>
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>Website</div>
            <InputText
              name="website"
              value={googleConfig?.website ?? ''}
              onChange={handleGoogleConfigChange}
              placeholder="https://example.com"
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>Phone Number</div>
            <InputText
              name="phoneNumber"
              value={googleConfig?.phoneNumber ?? ''}
              onChange={handleGoogleConfigChange}
              placeholder="+1 (555) 123-4567"
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-[40%] max-w-xl min-w-96">
            <div>YouTube Link</div>
            <InputText
              name="youTubeLink"
              value={googleConfig?.youTubeLink ?? ''}
              onChange={handleGoogleConfigChange}
              placeholder="https://youtube.com/watch?v=..."
              className="mt-3 p-inputtext-lg w-full"
            />
          </div>

          <div className="w-full">
            <div>Full Description</div>
            <InputTextarea
              name="appFullDescription"
              value={googleConfig?.appFullDescription ?? ''}
              onChange={handleGoogleConfigChange}
              placeholder="Detailed app description (max 4000 characters)"
              rows={4}
              maxLength={4000}
              className="mt-3 w-full"
            />
            <div className="text-xs text-gray-500 mt-1">
              {googleConfig?.appFullDescription?.length ?? 0}/4000 characters
            </div>
          </div>

          {/* Save Google Play Information button */}
          <div className="w-full">
            <div className="flex justify-end mt-6">
              <Button
                label="Save Google Play Information"
                icon="pi pi-save"
                className="text-2xl px-14 py-3 bg-[#F1B51C] hover:bg-bluegray-400 text-black border-none"
                onClick={saveGooglePlayInformation}
                loading={loading}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel header="📸 Screenshots">
        <div className="flex flex-col gap-y-6">
          <div className="w-full">
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-medium">Upload App Screenshots</div>
              <div className="flex gap-2">
                {/* Save button for changes */}
                {(googleSelectedImages.length > 0 ||
                  existingImages.some((img) => img.isDeleted) ||
                  googleHasOrderChanged) && (
                  <Button
                    label={
                      googleSelectedImages.length > 0 &&
                      existingImages.some((img) => img.isDeleted)
                        ? 'Save Changes'
                        : googleSelectedImages.length > 0
                          ? 'Save Google Play Screenshots'
                          : googleHasOrderChanged
                            ? 'Save Order Changes'
                            : 'Apply Deletions'
                    }
                    icon="pi pi-save"
                    className="text-2xl px-14 py-3 bg-green-500 hover:bg-green-600 text-white border-none"
                    onClick={saveGooglePlayScreenshots}
                    loading={loading}
                    disabled={loading}
                  />
                )}
                {googleSelectedImages.length > 0 && (
                  <Button
                    label="Clear All"
                    icon="pi pi-times"
                    className="p-button-sm p-button-outlined p-button-danger"
                    onClick={handleClearAllGoogleImages}
                  />
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              You can upload multiple screenshots for Google Play Store.
              Supported formats: JPG, PNG, GIF
            </p>
            <FileUpload
              key={googleUploadKey}
              ref={googleFileUploadRef}
              mode="basic"
              name="google-screenshots"
              multiple
              accept="image/*"
              onSelect={handleGoogleScreenshotsSelect}
              chooseLabel="Choose Screenshots"
              className="w-full"
              auto={false}
            />
          </div>

          {/* New Screenshots to Upload - Show First */}
          {googleSelectedImages.length > 0 && (
            <div className="w-full">
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-medium">
                  New Screenshots to Upload ({googleSelectedImages.length})
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {googleSelectedImages.map(
                  (image: SelectedImage, index: number) => (
                    <div
                      key={index}
                      className="relative bg-green-50 border border-green-200 rounded-lg overflow-hidden group"
                    >
                      {/* Image Preview */}
                      <div className="aspect-[9/16] w-full relative">
                        <img
                          src={image.previewUrl}
                          alt={image.name}
                          className="w-full h-full object-cover"
                        />
                        {/* Remove Button Overlay */}
                        <button
                          type="button"
                          onClick={() => {
                            handleRemoveGoogleImage(index);
                          }}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                      {/* Image Info */}
                      <div className="p-2">
                        <div className="text-xs font-medium text-green-800 truncate">
                          {image.name}
                        </div>
                        <div className="text-xs text-green-600">
                          Screenshot {index + 1}
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {/* Existing Screenshots from Server - Show After New Screenshots */}
          {existingImages.filter((img) => !img.isDeleted).length > 0 && (
            <div className="w-full">
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-medium">
                  Current Screenshots (
                  {existingImages.filter((img) => !img.isDeleted).length})
                </div>
                <div className="text-sm text-gray-600">
                  Drag images to reorder or use ↑↓ buttons
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {existingImages
                  .filter((img) => !img.isDeleted)
                  .map((image: ExistingImage, displayIndex: number) => (
                    <div
                      key={image.index}
                      className={`relative rounded-lg overflow-hidden group cursor-move transition-all duration-200 ${
                        googleDraggedIndex === displayIndex
                          ? 'opacity-50 scale-95'
                          : googleDragOverIndex === displayIndex
                            ? 'bg-blue-100 border-2 border-blue-300 scale-105'
                            : 'bg-emerald-50 border border-emerald-200'
                      }`}
                      draggable
                      onDragStart={(e) => {
                        handleGooglePlayDragStart(e, displayIndex);
                      }}
                      onDragEnter={(e) => {
                        handleGooglePlayDragEnter(e, displayIndex);
                      }}
                      onDragOver={handleGooglePlayDragOver}
                      onDragLeave={handleGooglePlayDragLeave}
                      onDrop={(e) => {
                        handleGooglePlayDrop(e, displayIndex);
                      }}
                      onDragEnd={handleGooglePlayDragEnd}
                    >
                      {/* Drag Handle Indicator */}
                      <div className="absolute top-1 left-1 bg-gray-700 bg-opacity-75 text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                        ⋮⋮
                      </div>

                      {/* Image Preview */}
                      <div className="aspect-[9/16] w-full relative">
                        <img
                          src={image.url}
                          alt={image.name}
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                        {/* Order Controls */}
                        <div className="absolute top-2 left-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveGooglePlayImageUp(displayIndex);
                            }}
                            disabled={displayIndex === 0}
                            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded w-6 h-6 flex items-center justify-center text-xs"
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveGooglePlayImageDown(displayIndex);
                            }}
                            disabled={
                              displayIndex ===
                              existingImages.filter((img) => !img.isDeleted)
                                .length -
                                1
                            }
                            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded w-6 h-6 flex items-center justify-center text-xs"
                            title="Move down"
                          >
                            ↓
                          </button>
                        </div>
                        {/* Remove Button Overlay */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveExistingImage(image.index);
                          }}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          title="Mark for deletion"
                        >
                          ×
                        </button>
                      </div>
                      {/* Image Info */}
                      <div className="p-2">
                        <div className="text-xs font-medium text-emerald-800 truncate">
                          {image.name}
                        </div>
                        <div className="text-xs text-emerald-600">
                          Order: {displayIndex + 1}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </TabPanel>

      <TabPanel header="🗑️ Delete Account Page Screenshot">
        <div className="flex flex-col gap-y-6">
          <div className="w-full">
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-medium">
                Upload Delete Account Page Screenshots
              </div>
              <div className="flex gap-2">
                {/* Save button for changes */}
                {(deleteAccountSelectedImages.length > 0 ||
                  deleteAccountExistingImages.some((img) => img.isDeleted) ||
                  hasOrderChanged) && (
                  <Button
                    label={
                      deleteAccountSelectedImages.length > 0 &&
                      deleteAccountExistingImages.some((img) => img.isDeleted)
                        ? 'Save Changes'
                        : deleteAccountSelectedImages.length > 0
                          ? 'Save Delete Account Screenshots'
                          : hasOrderChanged
                            ? 'Save Order Changes'
                            : 'Apply Deletions'
                    }
                    icon="pi pi-save"
                    className="text-2xl px-14 py-3 bg-green-500 hover:bg-green-600 text-white border-none"
                    onClick={saveDeleteAccountScreenshots}
                    loading={loading}
                    disabled={loading}
                  />
                )}
                {deleteAccountSelectedImages.length > 0 && (
                  <Button
                    label="Clear All"
                    icon="pi pi-times"
                    className="p-button-sm p-button-outlined p-button-danger"
                    onClick={handleClearAllDeleteAccountImages}
                  />
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Upload screenshots showing how users can delete their account in
              your app. Screenshots should be 240x240 pixels. You can reorder
              existing screenshots. Supported formats: JPG, PNG, GIF
            </p>
            <FileUpload
              key={deleteAccountUploadKey}
              ref={deleteAccountFileUploadRef}
              mode="basic"
              name="delete-account-screenshots"
              multiple
              accept="image/*"
              onSelect={handleDeleteAccountScreenshotsSelect}
              chooseLabel="Choose Screenshots"
              className="w-full"
              auto={false}
            />
          </div>

          {/* New Screenshots to Upload - Show First */}
          {deleteAccountSelectedImages.length > 0 && (
            <div className="w-full">
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-medium">
                  New Screenshots to Upload (
                  {deleteAccountSelectedImages.length})
                </div>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {deleteAccountSelectedImages.map(
                  (image: SelectedImage, index: number) => (
                    <div
                      key={index}
                      className="relative bg-green-50 border border-green-200 rounded-lg overflow-hidden group"
                    >
                      {/* Image Preview - Square aspect ratio for delete account screenshots */}
                      <div className="aspect-square w-full relative">
                        <img
                          src={image.previewUrl}
                          alt={image.name}
                          className="w-full h-full object-cover"
                        />
                        {/* Remove Button Overlay */}
                        <button
                          type="button"
                          onClick={() => {
                            handleRemoveDeleteAccountImage(index);
                          }}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                      {/* Image Info */}
                      <div className="p-2">
                        <div className="text-xs font-medium text-green-800 truncate">
                          {image.name}
                        </div>
                        <div className="text-xs text-green-600">
                          Delete Account Screenshot {index + 1}
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {/* Existing Screenshots from Server with Order Controls */}
          {deleteAccountExistingImages.filter((img) => !img.isDeleted).length >
            0 && (
            <div className="w-full">
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-medium">
                  Current Screenshots (
                  {
                    deleteAccountExistingImages.filter((img) => !img.isDeleted)
                      .length
                  }
                  )
                </div>
                <div className="text-sm text-gray-600">
                  Drag images to reorder or use ↑↓ buttons
                </div>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {deleteAccountExistingImages
                  .filter((img) => !img.isDeleted)
                  .map((image: ExistingImage, displayIndex: number) => (
                    <div
                      key={image.index}
                      className={`relative rounded-lg overflow-hidden group cursor-move transition-all duration-200 ${
                        draggedIndex === displayIndex
                          ? 'opacity-50 scale-95'
                          : dragOverIndex === displayIndex
                            ? 'bg-blue-100 border-2 border-blue-300 scale-105'
                            : 'bg-emerald-50 border border-emerald-200'
                      }`}
                      draggable
                      onDragStart={(e) => {
                        handleDragStart(e, displayIndex);
                      }}
                      onDragEnter={(e) => {
                        handleDragEnter(e, displayIndex);
                      }}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => {
                        handleDrop(e, displayIndex);
                      }}
                      onDragEnd={handleDragEnd}
                    >
                      {/* Drag Handle Indicator */}
                      <div className="absolute top-1 left-1 bg-gray-700 bg-opacity-75 text-white text-xs px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                        ⋮⋮
                      </div>

                      {/* Image Preview - Square aspect ratio for delete account screenshots */}
                      <div className="aspect-square w-full relative">
                        <img
                          src={image.url}
                          alt={image.name}
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                        {/* Order Controls */}
                        <div className="absolute top-2 left-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveDeleteAccountImageUp(displayIndex);
                            }}
                            disabled={displayIndex === 0}
                            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded w-6 h-6 flex items-center justify-center text-xs"
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveDeleteAccountImageDown(displayIndex);
                            }}
                            disabled={
                              displayIndex ===
                              deleteAccountExistingImages.filter(
                                (img) => !img.isDeleted,
                              ).length -
                                1
                            }
                            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded w-6 h-6 flex items-center justify-center text-xs"
                            title="Move down"
                          >
                            ↓
                          </button>
                        </div>
                        {/* Remove Button Overlay */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveDeleteAccountExistingImage(image.index);
                          }}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          title="Mark for deletion"
                        >
                          ×
                        </button>
                      </div>
                      {/* Image Info */}
                      <div className="p-2">
                        <div className="text-xs font-medium text-emerald-800 truncate">
                          {image.name}
                        </div>
                        <div className="text-xs text-emerald-600">
                          Order: {displayIndex + 1}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </TabPanel>

      <TabPanel header="🎨 Feature Graphic">
        <div className="flex flex-col gap-y-6">
          <div className="w-full">
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-medium">Feature Graphic</div>
              <div className="flex gap-2">
                {/* Save button for changes */}
                {(!!featureGraphicSelectedImage ||
                  (featureGraphicExistingImage &&
                    featureGraphicExistingImage.isDeleted)) && (
                  <Button
                    label={
                      featureGraphicSelectedImage &&
                      featureGraphicExistingImage?.isDeleted
                        ? 'Save Changes'
                        : featureGraphicSelectedImage
                          ? 'Save Feature Graphic'
                          : 'Apply Deletion'
                    }
                    icon="pi pi-save"
                    className="text-2xl px-14 py-3 bg-green-500 hover:bg-green-600 text-white border-none"
                    onClick={saveFeatureGraphic}
                    loading={loading}
                    disabled={loading}
                  />
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Upload a high-quality graphic that represents your app (1024 x 500
              pixels). Only one feature graphic is allowed. Supported formats:
              JPG, PNG, GIF
            </p>

            {/* Only show upload if no image is selected and no existing image */}
            {!featureGraphicSelectedImage && !featureGraphicExistingImage && (
              <FileUpload
                key={featureGraphicUploadKey}
                ref={featureGraphicFileUploadRef}
                mode="basic"
                name="feature-graphic"
                accept="image/*"
                onSelect={handleFeatureGraphicSelect}
                chooseLabel="Choose Feature Graphic"
                className="w-full"
                auto={false}
              />
            )}
          </div>

          {/* New Feature Graphic to Upload */}
          {featureGraphicSelectedImage && (
            <div className="w-full">
              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-medium">
                  New Feature Graphic to Upload
                </div>
              </div>
              <div className="flex justify-center">
                <div className="relative bg-green-50 border border-green-200 rounded-lg overflow-hidden group max-w-md">
                  {/* Image Preview - Feature graphic aspect ratio (1024x500) */}
                  <div className="aspect-[1024/500] w-full relative">
                    <img
                      src={featureGraphicSelectedImage.previewUrl}
                      alt={featureGraphicSelectedImage.name}
                      className="w-full h-full object-cover"
                    />
                    {/* Remove Button Overlay */}
                    <button
                      type="button"
                      onClick={handleRemoveFeatureGraphicImage}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      title="Remove image"
                    >
                      ×
                    </button>
                  </div>
                  {/* Image Info */}
                  <div className="p-3">
                    <div className="text-sm font-medium text-green-800 truncate">
                      {featureGraphicSelectedImage.name}
                    </div>
                    <div className="text-xs text-green-600">
                      New Feature Graphic
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Existing Feature Graphic from Server */}
          {featureGraphicExistingImage &&
            !featureGraphicExistingImage.isDeleted &&
            !featureGraphicSelectedImage && (
              <div className="w-full">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-lg font-medium">
                    Current Feature Graphic
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="relative bg-emerald-50 border border-emerald-200 rounded-lg overflow-hidden group max-w-md">
                    {/* Image Preview - Feature graphic aspect ratio (1024x500) */}
                    <div className="aspect-[1024/500] w-full relative">
                      <img
                        src={featureGraphicExistingImage.url}
                        alt={featureGraphicExistingImage.name}
                        className="w-full h-full object-cover"
                      />
                      {/* Remove Button Overlay */}
                      <button
                        type="button"
                        onClick={handleRemoveFeatureGraphicExistingImage}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        title="Mark for deletion"
                      >
                        ×
                      </button>
                    </div>
                    {/* Image Info */}
                    <div className="p-3">
                      <div className="text-sm font-medium text-emerald-800 truncate">
                        {featureGraphicExistingImage.name}
                      </div>
                      <div className="text-xs text-emerald-600">
                        Server Image
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Show upload area again if existing image is marked for deletion */}
          {featureGraphicExistingImage &&
            featureGraphicExistingImage.isDeleted &&
            !featureGraphicSelectedImage && (
              <div className="w-full">
                <div className="text-center py-6">
                  <div className="text-gray-500 mb-4">
                    <i className="pi pi-image text-3xl"></i>
                  </div>
                  <div className="text-lg font-medium text-gray-700 mb-2">
                    Current feature graphic marked for deletion
                  </div>
                  <div className="text-sm text-gray-500 mb-4">
                    Upload a new feature graphic or save to apply deletion
                  </div>
                  <FileUpload
                    key={featureGraphicUploadKey}
                    ref={featureGraphicFileUploadRef}
                    mode="basic"
                    name="feature-graphic"
                    accept="image/*"
                    onSelect={handleFeatureGraphicSelect}
                    chooseLabel="Choose New Feature Graphic"
                    className="w-full"
                    auto={false}
                  />
                </div>
              </div>
            )}
        </div>
      </TabPanel>
    </TabView>
  );
}
